import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT = '#1B5E20';
const VERT_CLAIR = '#E8F5E9';
const ROUGE = '#C62828';
const ROUGE_CLAIR = '#FFEBEE';
const GRIS_BG = '#f7f7f7';
const GRIS_TXT = '#616161';
const BORD = '#E0E0E0';
const ORANGE = '#E65100';
const ORANGE_CLAIR = '#FFF3E0';

const LIBELLES_MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const STATUT_META = {
  PRET: { label: 'Prêt', couleur: VERT, fond: VERT_CLAIR },
  A_COMPLETER: { label: 'À compléter', couleur: ORANGE, fond: ORANGE_CLAIR },
  BLOQUE: { label: 'Bloqué', couleur: ROUGE, fond: ROUGE_CLAIR },
};

function fmtMontant(v) {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toLocaleString('fr-FR')} FCFA`;
}

function Badge({ statut }) {
  const meta = STATUT_META[statut] || { label: statut || '—', couleur: GRIS_TXT, fond: GRIS_BG };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, color: meta.couleur, background: meta.fond }}>
      {meta.label}
    </span>
  );
}

function Modal({ titre, onFermer, largeur = 640, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto', zIndex: 1000 }} onClick={onFermer}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: largeur, padding: 24, marginTop: 24 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#1a1a1a' }}>{titre}</h3>
          <button onClick={onFermer} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: GRIS_TXT }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Modale de création de période ───────────────────────────────────────────
function ModaleNouvellePeriode({ onFermer, onCreee }) {
  const aujourdhui = new Date();
  const [mois, setMois] = useState(aujourdhui.getMonth() + 1);
  const [annee, setAnnee] = useState(aujourdhui.getFullYear());
  const [libelle, setLibelle] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const soumettre = async () => {
    setErreur(''); setEnCours(true);
    try {
      const res = await axios.post(`${API}/paie/preparation/periodes`, { mois, annee, libelle: libelle || undefined });
      onCreee(res.data.data);
    } catch (e) {
      setErreur(e.response?.data?.message || 'Impossible de créer la période.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modal titre="Nouvelle période de préparation" onFermer={onFermer} largeur={440}>
      {erreur && <div style={{ background: ROUGE_CLAIR, color: ROUGE, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{erreur}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 12, color: GRIS_TXT, display: 'block', marginBottom: 4 }}>Mois</label>
          <select value={mois} onChange={(e) => setMois(Number(e.target.value))} style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${BORD}` }}>
            {LIBELLES_MOIS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: GRIS_TXT, display: 'block', marginBottom: 4 }}>Année</label>
          <input type="number" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${BORD}` }} />
        </div>
      </div>
      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 12, color: GRIS_TXT, display: 'block', marginBottom: 4 }}>Libellé (optionnel)</label>
        <input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder={`${LIBELLES_MOIS[mois - 1]} ${annee}`} style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${BORD}` }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onFermer} style={{ padding: '10px 18px', borderRadius: 8, border: `1px solid ${BORD}`, background: '#fff', cursor: 'pointer' }}>Annuler</button>
        <button onClick={soumettre} disabled={enCours} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: VERT, color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
          {enCours ? 'Création…' : 'Créer la période'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Modale de sélection d'employés ──────────────────────────────────────────
function ModaleSelectionEmployes({ onFermer, onLancer }) {
  const [employes, setEmployes] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [selection, setSelection] = useState(new Set());
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    axios.get(`${API}/employes`, { params: { page: 1, limite: 1000 } })
      .then((res) => {
        const data = res.data;
        const liste = Array.isArray(data) ? data : (data.employes || data.items || data.data || []);
        setEmployes(liste.filter((e) => e.statut === 'Actif'));
      })
      .finally(() => setChargement(false));
  }, []);

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return employes;
    return employes.filter((e) => `${e.matricule} ${e.nom} ${e.prenom}`.toLowerCase().includes(q));
  }, [employes, recherche]);

  const toggle = (id) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Modal titre="Sélectionner les employés à préparer" onFermer={onFermer} largeur={560}>
      <input
        value={recherche} onChange={(e) => setRecherche(e.target.value)}
        placeholder="Rechercher par matricule ou nom…"
        style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${BORD}`, marginBottom: 12 }}
      />
      <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${BORD}`, borderRadius: 8 }}>
        {chargement && <div style={{ padding: 16, color: GRIS_TXT }}>Chargement…</div>}
        {!chargement && filtres.length === 0 && <div style={{ padding: 16, color: GRIS_TXT }}>Aucun employé trouvé.</div>}
        {filtres.map((e) => (
          <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${BORD}`, cursor: 'pointer' }}>
            <input type="checkbox" checked={selection.has(e.id)} onChange={() => toggle(e.id)} />
            <span style={{ fontWeight: 600, minWidth: 110 }}>{e.matricule}</span>
            <span>{e.prenom} {e.nom}</span>
            <span style={{ marginLeft: 'auto', color: GRIS_TXT, fontSize: 12 }}>{e.poste || 'Poste non renseigné'}</span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
        <span style={{ color: GRIS_TXT, fontSize: 13 }}>{selection.size} employé(s) sélectionné(s)</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onFermer} style={{ padding: '10px 18px', borderRadius: 8, border: `1px solid ${BORD}`, background: '#fff', cursor: 'pointer' }}>Annuler</button>
          <button
            onClick={() => onLancer(Array.from(selection))}
            disabled={selection.size === 0}
            style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: selection.size ? VERT : '#9E9E9E', color: '#fff', cursor: selection.size ? 'pointer' : 'not-allowed', fontWeight: 600 }}
          >
            Lancer la préparation
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Fiche détaillée d'un employé ────────────────────────────────────────────
function Section({ titre, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.04em', color: GRIS_TXT, margin: '0 0 8px' }}>{titre}</h4>
      {children}
    </div>
  );
}
function Champ({ label, valeur }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={{ fontSize: 11, color: GRIS_TXT, textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{valeur ?? '—'}</div>
    </div>
  );
}

function ModaleFiche({ periodeId, employeId, onFermer }) {
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    axios.get(`${API}/paie/preparation/periodes/${periodeId}/preparations/${employeId}`)
      .then((res) => setData(res.data.data))
      .finally(() => setChargement(false));
  }, [periodeId, employeId]);

  if (chargement) return <Modal titre="Fiche de préparation" onFermer={onFermer}><div style={{ padding: 20, color: GRIS_TXT }}>Chargement…</div></Modal>;
  if (!data) return <Modal titre="Fiche de préparation" onFermer={onFermer}><div style={{ padding: 20, color: GRIS_TXT }}>Aucune donnée.</div></Modal>;

  const snap = data.snapshot || {};
  const dossier = snap.dossier_salarial;
  const alertes = snap.alertes || [];

  return (
    <Modal titre={`${data.prenom} ${data.nom} — ${data.matricule}`} onFermer={onFermer} largeur={720}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <Badge statut={data.statut_completude} />
        {data.obsolete && <span style={{ fontSize: 12, color: ORANGE, fontWeight: 600 }}>Préparation obsolète — une source a changé depuis, actualisation recommandée</span>}
      </div>

      <Section titre="Rémunération fixe de référence">
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Champ label="Salaire de base" valeur={fmtMontant(data.salaire_base)} />
          <Champ label="Sursalaire validé" valeur={fmtMontant(data.sursalaire)} />
          <Champ label="Total (référence)" valeur={fmtMontant(data.remuneration_fixe_reference)} />
        </div>
        <p style={{ fontSize: 12, color: GRIS_TXT, marginTop: 8 }}>Ceci n'est pas le brut ni le net à payer — uniquement salaire de base + sursalaire validés, avant tout calcul de paie.</p>
      </Section>

      {dossier && (
        <Section titre="Dossier salarial">
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Champ label="Famille" valeur={dossier.famille_professionnelle} />
            <Champ label="Catégorie" valeur={dossier.categorie} />
            <Champ label="Échelon" valeur={dossier.echelon} />
            <Champ label="Type de rémunération" valeur={dossier.type_remuneration} />
            <Champ label="Mode de paiement" valeur={dossier.mode_paiement} />
            <Champ label="Date d'effet" valeur={dossier.date_effet} />
            <Champ label="Source" valeur={dossier.source} />
          </div>
        </Section>
      )}

      <Section titre="Temps et absences (informatif — export de la clôture mensuelle)">
        {snap.temps && snap.temps.source === 'EXPORT_TEMPS' ? (
          <>
            <p style={{ fontSize: 13, color: GRIS_TXT }}>
              Export du temps, version {snap.temps.export.version} ({snap.temps.export.statut === 'INTEGRE' ? 'intégré en paie' : 'généré, non encore intégré'}).
            </p>
            {snap.temps.elements.length ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                {snap.temps.elements.map((e) => (
                  <Champ key={`${e.code_rubrique}-${e.origine}`}
                    label={`${e.code_rubrique}${e.origine === 'REGULARISATION' ? ' (régularisation)' : ''}`}
                    valeur={`${e.quantite} ${e.unite === 'HEURES' ? 'h' : e.unite === 'JOURS' ? 'j' : ''}`.trim()} />
                ))}
              </div>
            ) : <p style={{ fontSize: 13, color: GRIS_TXT }}>Aucun élément de temps pour ce salarié ce mois-ci.</p>}
          </>
        ) : <p style={{ fontSize: 13, color: GRIS_TXT }}>Aucun export de temps clôturé pour ce mois : la préparation ne s'appuie pas sur le temps.</p>}
      </Section>

      <Section titre="IPM (informatif — produit par le module IPM, jamais modifié par la paie)">
        {snap.ipm?.masque ? (
          <p style={{ fontSize: 13, color: GRIS_TXT }}>Réservé aux personnes ayant une habilitation IPM (contrôle RH, paie, financier ou gestion).</p>
        ) : snap.ipm?.profil_existant || snap.ipm?.retenue_periode || snap.ipm?.cotisation_periode ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            <Champ label="Dette IPM (restes à charge)" valeur={fmtMontant(snap.ipm.dette?.solde)} />
            {snap.ipm.retenue_periode?.montant_applicable != null
              ? <Champ label="Retenue IPM applicable (période arrêtée)" valeur={fmtMontant(snap.ipm.retenue_periode.montant_applicable)} />
              : <Champ label="Retenue IPM proposée (période non arrêtée)" valeur={fmtMontant(snap.ipm.retenue_periode?.montant_propose)} />}
            <Champ label="Cotisation salariale de la période" valeur={fmtMontant(snap.ipm.cotisation_periode?.montant)} />
          </div>
        ) : <p style={{ fontSize: 13, color: GRIS_TXT }}>Aucune donnée IPM pour cet employé sur cette période.</p>}
      </Section>

      <Section titre="Prêts et dettes (informatif)">
        {(snap.prets_dettes?.dettes?.length || snap.prets_dettes?.prets_sogas?.length) ? (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {(snap.prets_dettes.dettes || []).map((d) => <li key={d.id}>{d.type_dette_code} — {fmtMontant(d.mensualite)}/mois — reste {fmtMontant(d.montant_restant)}</li>)}
            {(snap.prets_dettes.prets_sogas || []).map((p) => <li key={p.id}>Prêt SOGAS {p.numero_dossier} — {fmtMontant(p.mensualite_retenue)}/mois{p.montant_restant != null ? <> — reste {fmtMontant(p.montant_restant)}</> : null}</li>)}
          </ul>
        ) : <p style={{ fontSize: 13, color: GRIS_TXT }}>Aucun prêt ni dette active.</p>}
      </Section>

      <Section titre="Éléments variables de paie">
        {snap.rubriques_actives?.length ? (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>{snap.rubriques_actives.map((r) => <li key={r.code}>{r.libelle}</li>)}</ul>
        ) : <p style={{ fontSize: 13, color: GRIS_TXT }}>Aucune rubrique de paie active — fonction disponible après activation du moteur des éléments variables de paie.</p>}
      </Section>

      <Section titre={`Alertes (${alertes.length})`}>
        {alertes.length === 0 && <p style={{ fontSize: 13, color: VERT }}>Aucune alerte.</p>}
        {alertes.map((a, i) => {
          const couleur = a.niveau === 'BLOQUANT' ? ROUGE : a.niveau === 'A_COMPLETER' ? ORANGE : GRIS_TXT;
          const fond = a.niveau === 'BLOQUANT' ? ROUGE_CLAIR : a.niveau === 'A_COMPLETER' ? ORANGE_CLAIR : GRIS_BG;
          return (
            <div key={i} style={{ background: fond, color: couleur, padding: '8px 12px', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
              {a.message}
            </div>
          );
        })}
      </Section>
    </Modal>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function PreparationPaiePage() {
  const { employe, hasPermission } = useAuth();
  const [periodes, setPeriodes] = useState([]);
  const [periodeId, setPeriodeId] = useState(null);
  const [preparations, setPreparations] = useState({ items: [], compteurs: {} });
  const [filtreStatut, setFiltreStatut] = useState('');
  const [chargement, setChargement] = useState(true);
  const [modaleNouvellePeriode, setModaleNouvellePeriode] = useState(false);
  const [modaleSelection, setModaleSelection] = useState(false);
  const [ficheOuverte, setFicheOuverte] = useState(null);
  const [enPreparation, setEnPreparation] = useState(false);

  const role = employe?.role;
  const peutCreerPeriode = ['drh', 'rh_principal'].includes(role) || hasPermission?.('peut_creer_periode_paie');
  const peutPreparer = ['drh', 'rh_principal'].includes(role) || hasPermission?.('peut_preparer_paie');

  const chargerPeriodes = useCallback(async () => {
    const res = await axios.get(`${API}/paie/preparation/periodes`);
    const liste = res.data.data || [];
    setPeriodes(liste);
    if (!periodeId && liste.length > 0) setPeriodeId(liste[0].id);
  }, [periodeId]);

  const chargerPreparations = useCallback(async () => {
    if (!periodeId) { setPreparations({ items: [], compteurs: {} }); return; }
    setChargement(true);
    try {
      const res = await axios.get(`${API}/paie/preparation/periodes/${periodeId}/preparations`, {
        params: filtreStatut ? { statut_completude: filtreStatut } : {},
      });
      setPreparations(res.data.data);
    } finally {
      setChargement(false);
    }
  }, [periodeId, filtreStatut]);

  useEffect(() => { chargerPeriodes(); }, [chargerPeriodes]);
  useEffect(() => { chargerPreparations(); }, [chargerPreparations]);

  const lancerPreparation = async (employeIds) => {
    setModaleSelection(false);
    setEnPreparation(true);
    try {
      await axios.post(`${API}/paie/preparation/periodes/${periodeId}/preparer`, { employe_ids: employeIds });
      await chargerPreparations();
      await chargerPeriodes();
    } finally {
      setEnPreparation(false);
    }
  };

  const periodeCourante = periodes.find((p) => p.id === periodeId);
  const compteurs = preparations.compteurs || {};

  return (
    <Layout>
      <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, margin: 0, color: '#1a1a1a' }}>Préparation de la paie</h1>
            <p style={{ fontSize: 13, color: GRIS_TXT, margin: '4px 0 0' }}>
              Vérifiez avant tout calcul quels employés sont prêts, à compléter ou bloqués. Aucune paie n'est calculée ici.
            </p>
          </div>
          {peutCreerPeriode && (
            <button onClick={() => setModaleNouvellePeriode(true)} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: VERT, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
              Nouvelle période
            </button>
          )}
        </div>

        {/* Sélecteur de périodes */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 16 }}>
          {periodes.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriodeId(p.id)}
              style={{
                padding: '8px 16px', borderRadius: 20, border: `1px solid ${p.id === periodeId ? VERT : BORD}`,
                background: p.id === periodeId ? VERT_CLAIR : '#fff', color: p.id === periodeId ? VERT : '#333',
                fontWeight: p.id === periodeId ? 700 : 500, whiteSpace: 'nowrap', cursor: 'pointer', fontSize: 13,
              }}
            >
              {p.libelle} <span style={{ opacity: 0.7 }}>({p.statut})</span>
            </button>
          ))}
          {periodes.length === 0 && <span style={{ color: GRIS_TXT, fontSize: 13 }}>Aucune période créée pour l'instant.</span>}
        </div>

        {periodeCourante && (
          <>
            {/* Compteurs + filtres */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                { key: '', label: 'Tous', n: compteurs.total },
                { key: 'PRET', label: 'Prêts', n: compteurs.prets },
                { key: 'A_COMPLETER', label: 'À compléter', n: compteurs.a_completer },
                { key: 'BLOQUE', label: 'Bloqués', n: compteurs.bloques },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFiltreStatut(f.key)}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: `1px solid ${filtreStatut === f.key ? VERT : BORD}`,
                    background: filtreStatut === f.key ? VERT : '#fff', color: filtreStatut === f.key ? '#fff' : '#333',
                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  }}
                >
                  {f.label} ({f.n ?? 0})
                </button>
              ))}
              {peutPreparer && (
                <button onClick={() => setModaleSelection(true)} disabled={enPreparation} style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, border: 'none', background: VERT, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  {enPreparation ? 'Préparation en cours…' : 'Préparer des employés'}
                </button>
              )}
            </div>

            {/* Tableau desktop */}
            <div className="prep-paie-table" style={{ overflowX: 'auto', border: `1px solid ${BORD}`, borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: GRIS_BG, textAlign: 'left' }}>
                    {['Matricule', 'Employé', 'Poste', 'Catégorie', 'Salaire de base', 'Sursalaire', 'Rémunération fixe', 'Statut', 'Alertes', 'Actualisé le'].map((h) => (
                      <th key={h} style={{ padding: '10px 12px', fontWeight: 600, color: GRIS_TXT, textTransform: 'uppercase', fontSize: 11, letterSpacing: '.03em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chargement && <tr><td colSpan={10} style={{ padding: 20, textAlign: 'center', color: GRIS_TXT }}>Chargement…</td></tr>}
                  {!chargement && preparations.items.length === 0 && (
                    <tr><td colSpan={10} style={{ padding: 20, textAlign: 'center', color: GRIS_TXT }}>Aucun employé préparé pour cette période. Utilisez « Préparer des employés ».</td></tr>
                  )}
                  {preparations.items.map((it) => (
                    <tr key={it.id} style={{ borderTop: `1px solid ${BORD}`, cursor: 'pointer' }} onClick={() => setFicheOuverte(it.employe_id)}>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{it.matricule}</td>
                      <td style={{ padding: '10px 12px' }}>{it.prenom} {it.nom}</td>
                      <td style={{ padding: '10px 12px', color: it.poste ? '#333' : GRIS_TXT }}>{it.poste || 'Poste non renseigné'}</td>
                      <td style={{ padding: '10px 12px' }}>{it.categorie || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>{fmtMontant(it.salaire_base)}</td>
                      <td style={{ padding: '10px 12px' }}>{fmtMontant(it.sursalaire)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>{fmtMontant(it.remuneration_fixe_reference)}</td>
                      <td style={{ padding: '10px 12px' }}><Badge statut={it.statut_completude} /></td>
                      <td style={{ padding: '10px 12px' }}>{it.nb_alertes > 0 ? <span style={{ color: ORANGE, fontWeight: 600 }}>{it.nb_alertes}</span> : '—'}</td>
                      <td style={{ padding: '10px 12px', color: GRIS_TXT, fontSize: 12 }}>{it.resolu_le ? new Date(it.resolu_le).toLocaleString('fr-FR') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cartes mobile */}
            <div className="prep-paie-cards">
              {preparations.items.map((it) => (
                <div key={it.id} onClick={() => setFicheOuverte(it.employe_id)} style={{ background: '#fff', border: `1px solid ${BORD}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{it.prenom} {it.nom}</div>
                      <div style={{ fontSize: 12, color: GRIS_TXT }}>{it.matricule} — {it.poste || 'Poste non renseigné'}</div>
                    </div>
                    <Badge statut={it.statut_completude} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div><div style={{ fontSize: 10, color: GRIS_TXT, textTransform: 'uppercase' }}>Base</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMontant(it.salaire_base)}</div></div>
                    <div><div style={{ fontSize: 10, color: GRIS_TXT, textTransform: 'uppercase' }}>Sursalaire</div><div style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMontant(it.sursalaire)}</div></div>
                  </div>
                  <div style={{ marginTop: 8, fontWeight: 700 }}>{fmtMontant(it.remuneration_fixe_reference)}{it.nb_alertes > 0 && <span style={{ marginLeft: 8, color: ORANGE, fontSize: 12, fontWeight: 600 }}>{it.nb_alertes} alerte(s)</span>}</div>
                </div>
              ))}
              {!chargement && preparations.items.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: GRIS_TXT }}>Aucun employé préparé pour cette période.</div>}
            </div>
          </>
        )}

        {periodes.length === 0 && !chargement && (
          <div style={{ padding: 40, textAlign: 'center', color: GRIS_TXT, background: '#fff', border: `1px solid ${BORD}`, borderRadius: 10 }}>
            Aucune période de préparation n'existe encore.
            {peutCreerPeriode && <div style={{ marginTop: 12 }}><button onClick={() => setModaleNouvellePeriode(true)} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: VERT, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Créer la première période</button></div>}
          </div>
        )}
      </div>

      <style>{`
        .prep-paie-cards { display: none; }
        @media (max-width: 900px) {
          .prep-paie-table { display: none; }
          .prep-paie-cards { display: block; }
        }
      `}</style>

      {modaleNouvellePeriode && (
        <ModaleNouvellePeriode onFermer={() => setModaleNouvellePeriode(false)} onCreee={(p) => { setModaleNouvellePeriode(false); setPeriodeId(p.id); chargerPeriodes(); }} />
      )}
      {modaleSelection && (
        <ModaleSelectionEmployes onFermer={() => setModaleSelection(false)} onLancer={lancerPreparation} />
      )}
      {ficheOuverte && (
        <ModaleFiche periodeId={periodeId} employeId={ficheOuverte} onFermer={() => setFicheOuverte(null)} />
      )}
    </Layout>
  );
}
